import React from 'react'
import { PersonRemove } from '@fs/zion-icon'
import { Button, Row, Separator, H3, Overline, Subheading, useOverlay, FlowGrid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSlsDispatchContext, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import NavigationButtonYes from '../../../../components/sharedReviewComponents/navigationButtonYes/NavigationButtonYes'
import NavigationButtonNo from '../../../../components/sharedReviewComponents/navigationButtonNo/NavigationButtonNo'
import NavigationButtonUnsure from '../../../../components/sharedReviewComponents/navigationButtonUnsure/NavigationButtonUnsure'
import { getPrimaryName, getAllPersons, getPrimaryOrFirstPerson } from '../../utils/utils'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import { deletePerson, savePrimaryPerson } from '../../utils/saveFunctions'
import RecordTooltip from './RecordTooltip'
import HelpWithThisStep from '../faqContent/HelpWithThisStep'
import RecordTooltipButton from './RecordTooltipButton'

export default function StepOne({
  currentTask,
  loading,
  setStepTwoData,
  stepTwoOverlay,
  skipTask,
  stepOnePartTwoOverlay,
  hasImagePermission,
  handleGoToNextRecordAndResetStep,
  setSaveUpdateCalled,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const state = useFamilyReviewStateContext()
  const dispatch = useFamilyReviewDispatchContext()
  const zionSLSState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const recordTooltipOverlay = useOverlay()

  const handleClickYes = async () => {
    const primaryPerson = getPrimaryOrFirstPerson(currentTask)
    if (!primaryPerson.primary) {
      await savePrimaryPerson({
        primaryPerson,
        currentTask,
        partitionKey: currentTask.partitionKey,
        zionSLSState,
        zionSlsDispatch,
        userId: state.userId,
        saveChangesFlag,
      })
      setSaveUpdateCalled(true)
    }
    setStepTwoData()
    stepTwoOverlay.open()
    dispatch({ type: 'SET_CURRENT_STEP', value: STEPS.STEP_TWO })
  }
  const handleClickNo = () => {
    stepOnePartTwoOverlay.open()
    h.clearDisplay({ all: true })
  }
  const handleNotAPerson = () => {
    const persons = getAllPersons(currentTask)
    deletePerson({
      partitionKey: currentTask.partitionKey,
      personId: getPrimaryOrFirstPerson(currentTask)?.id,
      zionSLSState,
      zionSlsDispatch,
      userId: state.userId,
      saveChangesFlag,
    })
    if (persons.length > 1) stepOnePartTwoOverlay.open()
    else if (persons.length === 1) {
      skipTask()
    }
  }
  const primaryPerson = getPrimaryName(currentTask)
  return (
    <>
      <H3 size="H5">
        <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '1' })}</Overline>
        {t('family-review.step1.header-q', 'Is this record primarily about {primaryPerson}?', { primaryPerson })}{' '}
        <RecordTooltipButton overlay={recordTooltipOverlay} stepText="One" />
        <Subheading>
          {t(
            'family-review.step1.content-subheader',
            'You can correct any highlight or spelling mistakes in the next step.'
          )}
        </Subheading>
      </H3>
      <RecordTooltip overlay={recordTooltipOverlay} />
      <Separator size="sm" />
      <FlowGrid columnCount={3}>
        <NavigationButtonYes onClick={handleClickYes} loading={loading} linkName="Step One Yes" />
        <NavigationButtonNo onClick={handleClickNo} loading={loading} linkName="Step One No" />
        <NavigationButtonUnsure onClick={skipTask} loading={loading} linkName="Step One Unsure" />
      </FlowGrid>
      <Separator />
      <Row>
        <Button
          Icon={PersonRemove}
          linkName="Step One Not A Person"
          onClick={handleNotAPerson}
          color="blue"
          emphasis="low"
          size="sm"
          disabled={loading}
          data-testid="not-a-person-button"
        >
          {t('navigation.not-a-person-button', 'Not A Person')}
        </Button>
      </Row>
      <HelpWithThisStep
        currentStep="1"
        hasImagePermission={hasImagePermission}
        handleGoToNextRecordAndResetStep={handleGoToNextRecordAndResetStep}
        personId={getPrimaryOrFirstPerson(currentTask)?.id}
      />
    </>
  )
}
